import { css, SerializedStyles } from '@emotion/react';
import { ReactNode } from 'react';
import { buildPageSpacing, spacing, colors } from '../../styles';

interface Props {
  children: ReactNode;
  /** Additional CSS to apply to the fixed header container */
  headerCss?: SerializedStyles;
}

/**
 * A reusable fixed header component that handles:
 * - Fixed positioning at top of viewport
 * - Safe area insets for mobile
 * - Dark mode styling
 * - Spacer div to push content below the header
 */
const StickyHeader = ({ children, headerCss }: Props) => {
  return (
    <div
      css={css`
        /* Spacer to push content below fixed header.
         * The fixed header has: safe-area-inset-top + 8px padding + content + 8px padding + 0.5px border.
         * The Page component already provides top padding of (24px + safe-area-inset-top).
         * This spacer height = header_height - page_padding_top.
         * For content up to ~44px (e.g., ChatPage hamburger menu), we need:
         * (safe-area + 8 + 44 + 8 + 0.5) - (safe-area + 24) = 36.5px
         * Using hecto (40px) provides comfortable margin for typical header content. */
        height: ${spacing.hecto};
      `}
    >
      <div
        css={css`
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 102;
          padding: ${buildPageSpacing(spacing.centi, 'TOP')} ${buildPageSpacing(spacing.deci, 'RIGHT')} ${spacing.centi}
            ${buildPageSpacing(spacing.deci, 'LEFT')};
          border-bottom: 0.5px solid ${colors.cloud};
          background-color: ${colors.sugar};

          html[data-theme='dark'] & {
            border-bottom-color: ${colors.tungsten};
            background-color: ${colors.liquorice};
          }

          ${headerCss}
        `}
      >
        {children}
      </div>
    </div>
  );
};

export default StickyHeader;
